import * as HLF from "html-living-framework";
import {ArrayUtils, PlayMode} from "html-living-framework";
import * as PIXI from "pixi.js";
import {AbstractGameState} from "./AbstractGameState";
import {Container} from "../Core/Container";
import {ButtonMute} from "../GameObjects/ButtonMute";
import {CheckmarkButton, HomeButton} from "../GameObjects/Button";
import {StateName} from "../Data/StateName";
import {ItemType} from "../Data/ItemModel";
import {array} from "../Core/Reflection";
import {PuzzlePiece, PuzzlePiecePlaceholder} from "../GameObjects/Puzzle/PuzzlePiece";
import {EditablePhoto} from "../GameObjects/Puzzle/Photo";
import {main} from "../Layout/PuzzleScreenLayout";
import {PuzzleModel, PuzzleType} from "../GameObjects/Puzzle/PuzzleModel";
import {Sprite} from "../Core/Sprite";
import {PointUtil} from "../Core/Geom";
import {Ease} from "../Core/Animatable";
import {hud} from "../Layout/HudLayout";
import {VoiceOverManager} from "../Managers/VoiceOverManager";
import {Keyboard} from "../GameObjects/Keyboard";
import {AudioChannel} from "../Core/AudioChannel";
import {nextRandomVO, playVO, ReactionVO} from "../Data/VoiceOver";

export class PuzzleScreen extends AbstractGameState {

    public readonly main = this.addChild(new Container());
    public readonly background = this.main.addChild(new Sprite());
    public readonly puzzleBox = this.main.addChild(new Sprite());
    public readonly puzzleContainer = this.main.addChild(new Sprite());
    public readonly completePiecesContainer = this.main.addChild(new Container());
    public readonly draggingPiecesContainer = this.main.addChild(new Container());
    public readonly puzzlePieces = array((i: number) => this.completePiecesContainer.addChild(new PuzzlePiece(i + 1)));
    public readonly puzzleRandomPoints = array(() => ({x: 0, y: 0, width: 0, height: 0}));
    public readonly photo = this.main.addChild(new EditablePhoto());
    public readonly photoScaleUp = {x: 0, y: 0, width: 0, height: 0};

    public readonly buttonOk = this.main.addChild(new CheckmarkButton());
    public readonly buttonHome = this.main.addChild(new HomeButton());
    public readonly buttonMute = this.addChild(new ButtonMute());

    private model!: PuzzleModel;
    private reactionVO = [
        "LetsGoLuna_241",
        "LetsGoLuna_242",
        "LetsGoLuna_243",
        "LetsGoLuna_244"
    ];
    private puzzleStartVO = {
        [PuzzleType.Type1]: "LetsGoLuna_235",
        [PuzzleType.Type2]: "LetsGoLuna_214",
        [PuzzleType.Type3]: "LetsGoLuna_217",
        [PuzzleType.Type4]: "LetsGoLuna_220",
        [PuzzleType.Type5]: "LetsGoLuna_223",
        [PuzzleType.Type6]: "LetsGoLuna_226",
        [PuzzleType.Type7]: "LetsGoLuna_229",
        [PuzzleType.Type8]: "LetsGoLuna_232",
    };
    private puzzleFactVO = {
        [PuzzleType.Type1]: ["LetsGoLuna_236", "LetsGoLuna_237"],
        [PuzzleType.Type2]: ["LetsGoLuna_215", "LetsGoLuna_216"],
        [PuzzleType.Type3]: ["LetsGoLuna_218", "LetsGoLuna_219"],
        [PuzzleType.Type4]: ["LetsGoLuna_221", "LetsGoLuna_222"],
        [PuzzleType.Type5]: ["LetsGoLuna_224", "LetsGoLuna_225"],
        [PuzzleType.Type6]: ["LetsGoLuna_227", "LetsGoLuna_228"],
        [PuzzleType.Type7]: ["LetsGoLuna_230", "LetsGoLuna_231"],
        [PuzzleType.Type8]: ["LetsGoLuna_233", "LetsGoLuna_234"],
    };
    /**
     * 0 - not playing
     * 1 - already playing
     * 2 - scheduled to play #1
     * 2 - scheduled to play #2
     */
    private factVOStatus = 0;
    private completeVOStatus = 0;

    init(pSize: PIXI.Point, model: PuzzleModel) {
        super.init(pSize, model);

        this.model = model;

        main.call(this);
        hud.call(this);
        this.main.scale.set(0.5);

        this.puzzlePieces.forEach(it => it.setModel(model));
        this.photo.setModel(model);
        this.photo.visible = false;
        this.photo.button.onClick.add(() => this.startActivityCompleteIdle());

        this.initPuzzlePieces();

        this.buttonHome.onClick.add(() => {
            this.addItem(model, false);
        });
        this.buttonOk.onClick.add(() => {
            this.buttonOk.stopPulse();
            this.addItem(model, true);
        });
        this.buttonOk.visible = false;

        Keyboard.add(
            this.buttonHome,
            this.buttonOk,
            this.buttonMute
        );

        ArrayUtils.shuffle(this.reactionVO);
        playVO([this.puzzleStartVO[model.puzzleType], 0.5, "LetsGoLuna_239", 0.5, "LetsGoLuna_240"], () => {
            this.main.delayedCall(5, () => {
                this.playFactVO(0);
            });
        });
    }

    public onResize(pSize: PIXI.Point) {
        super.onResize(pSize);

        if (this.buttonMute) {
            this.buttonMute.setPosition(this._size);
        }
    }

    destroy(pOptions?: Parameters<typeof PIXI.Container.prototype.destroy>[0]) {
        VoiceOverManager.instance.stopVO();
        super.destroy(pOptions);
    }

    private initPuzzlePieces() {
        HLF.ArrayUtils.shuffle(this.puzzleRandomPoints);
        const k = HLF.Random.intBetween(0, this.puzzlePieces.length);
        for (let i = 0, j = 0; i < this.puzzlePieces.length; i++, j++) {
            if (i === k) {
                j--;
                continue;
            }
            const piece = this.puzzlePieces[i];
            const point = this.puzzleRandomPoints[j];
            const pieceHalfWidth = piece.width * 0.5;
            const pieceHalfHeight = piece.height * 0.5;
            piece.saveTransform();
            this.initPieceKeyboard(piece);
            piece.scale.set(0.75);
            piece.alignBy(point, 0.5, 0.5);
            const pieceScaleMax = piece.getTransformBackup().scaleX;
            const pieceScaleMin = piece.scale.x;
            this.draggingPiecesContainer.addChild(piece);
            piece.onStartDrag.add(() => {
                piece.zIndex = -1;
            });
            piece.onStopDrag.add(() => {
                if (PointUtil.distance(piece, piece.getTransformBackup()) < pieceHalfWidth * 0.5) {
                    this.placePuzzlePiece(piece);
                    AudioChannel.SFX.play("puzzle-place");
                    if (this.isPuzzleComplete()) {
                        AudioChannel.SFX.play("short-succes");
                    } else if (this.evenPuzzlePiece()) {
                        playVO(nextRandomVO(this.reactionVO), {mode: PlayMode.New});
                    }
                }
            });
            piece.onDrag.add(() => {
                const mx = 1024;
                const my = 768;
                piece.x = Math.max(Math.min(piece.x, mx - piece.width * 0.9), -mx - piece.width * 0.1);
                piece.y = Math.max(Math.min(piece.y, my - piece.height * 0.9), -my - piece.height * 0.1);
                const x = Math.abs(this.puzzleContainer.x + this.puzzleContainer.width * 0.5 - piece.x - pieceHalfWidth);
                const y = Math.abs(this.puzzleContainer.y + this.puzzleContainer.height * 0.5 - piece.y - pieceHalfHeight);
                const d = PointUtil.length(x, y);
                const min = this.puzzleContainer.height * 0.5;
                const max = this.puzzleContainer.height * 0.5 + pieceHalfHeight;
                piece.scale.set(d > max ? pieceScaleMin : d < min ? pieceScaleMax :
                    pieceScaleMax + (d - min) / (max - min) * (pieceScaleMin - pieceScaleMax));
            });
        }
        this.placePuzzlePiece(this.puzzlePieces[k]);
    }

    private initPieceKeyboard(piece: PuzzlePiece) {
        const button = this.completePiecesContainer.addChild(new PuzzlePiecePlaceholder(piece));
        piece.onDown.add(() => {
            this.puzzlePieces.forEach(it => it.keyboardSelected = false);
        });
        piece.onClick.add(() => {
            if (piece.keyboardSelected) {
                this.puzzlePieces.forEach(it => {
                    if (it !== piece) {
                        it.keyboardSelected = false;
                    }
                });
            }
        });
        button.onClick.add(() => {
            if (piece.keyboardSelected) {
                button.enabled = false;
                piece.keyboardSelected = false;
                piece.moveXYTo(button.x, button.y, 0.6, Ease.sineInOut, {
                    onUpdate: piece.onDrag,
                    onComplete: () => piece.onStopDrag(new PIXI.InteractionEvent())
                });
            }
        });
        Keyboard.add(piece, button);
    }

    private placePuzzlePiece(piece: PuzzlePiece) {
        this.completePiecesContainer.addChild(piece);
        piece.enabled = false;
        if (piece.placeholder) piece.placeholder.enabled = false;
        piece.tweenTo(0.1, Ease.sineInOut, {
            ...piece.getTransformBackup(),
            onComplete: () => {
                piece.background.changeParent(this.completePiecesContainer);
                piece.background.zIndex = 0;
                if (this.isPuzzleComplete()) {
                    Keyboard.focus(this.buttonHome);
                    this.onPuzzleComplete();
                } else {
                    Keyboard.focus(this.puzzlePieces.filter(it => it.enabled)[0]);
                }
            }
        });
    }

    private isPuzzleComplete() {
        return this.puzzlePieces.every(it => !it.enabled);
    }

    private evenPuzzlePiece() {
        return this.puzzlePieces.filter(it => !it.enabled).length % 2 === 0;
    }

    private onPuzzleComplete() {
        this.puzzleContainer.alphaTo(0, 0.2);
        this.completePiecesContainer.alphaTo(0, 0.2);
        this.photo.visible = true;
        this.photo.hideFrames();
        this.photo.alphaFrom(0, 0.2, () => {
            this.puzzleBox.moveXTo(-this._size.x - this.puzzleBox.width * 0.5, 0.4, Ease.sineIn);
            this.photo.tweenTo(0.6, Ease.sineInOut, {
                ...this.photoScaleUp,
                onComplete: () => {
                    this.photo.scaleTo(1, 0.6, Ease.sineInOut, {
                        delay: 1,
                        onComplete: () => this.photo.showFirstFrame()
                    });
                }
            })
        });
        this.playCompleteVO();
    }

    private playCompleteVO() {
        if (this.factVOStatus === 1) {
            this.completeVOStatus = 2;
            return;
        }
        this.completeVOStatus = 1;
        playVO([...ReactionVO(), "LetsGoLuna_245"], () => {
            if (this.isFrameVO()) {
                playVO("LetsGoLuna_246", () => this.onPlayCompleteVO());
            } else {
                this.onPlayCompleteVO();
            }
            this.buttonOk.visible = true;
            this.buttonOk.moveYFrom(this._size.y + this.buttonOk.height * 0.5, 0.4, Ease.backOut);
            this.buttonOk.pulse();
            Keyboard.focus(this.buttonOk);
        });
    }

    private onPlayCompleteVO() {
        this.completeVOStatus = 0;
        this.startActivityCompleteIdle();
        if (this.factVOStatus >= 2) {
            this.playFactVO(this.factVOStatus - 2);
        }
    }

    private playFactVO(index: number) {
        if (this.completeVOStatus === 1) {
            this.factVOStatus = 2 + index;
            return;
        }
        this.factVOStatus = 1;
        playVO(this.puzzleFactVO[this.model.puzzleType][index], () => {
            this.factVOStatus = 0;
            if (this.completeVOStatus === 2) {
                this.playCompleteVO();
            }
            if (index === 0) {
                this.main.delayedCall(15, () => {
                    this.playFactVO(1);
                });
            }
        });
    }

    private addItem(model: PuzzleModel, override: boolean) {
        // Always add a new item to change puzzles
        this.gameManager.addItem({
            itemType: ItemType.Puzzle,
            itemData: {
                puzzleType: model.puzzleType,
                puzzleFrame: this.photo.getSelectedFrame()
            }
        });
        this.gotoState(StateName.Home, {activityComplete: override});
    }

    getActivityCompleteIdleVO(): string {
        return this.isFrameVO() ? "LetsGoLuna_246" : super.getActivityCompleteIdleVO();
    }

    private isFrameVO() {
        return this.gameManager.nthPackageOfType(ItemType.Puzzle) <= 1;
    }
}